import {CertificateSigningRequest} from "./certificate-signing-request";
import {Status} from "./status";
import {Gateway} from "./gateway";
import {AmexAccountType} from "./amex-account";
import {getTargetSchemes, KnownRoutes} from "./known-routes";

export class LocationConfig {
  merchant: Merchant;
  location: Location;
  remoteTerminal?: Terminal;
}

export class Location {
  id: string;
  status: Status;
  routes: LocationRoute[];
  apiKeys?: LocationApiKey[];
  remoteTerminalId?: string;
  description?: string;
  domainNames?: string[];
  gatewayUsername?: string;
  encryptedGatewayPassword?: string;
  isMerchantInitiatedTransactionsEnabled?: boolean;
  isMotoEnabled?: boolean;
  isMotoAllowedWithoutCVV?: boolean;
  isApplePayEnabled?: boolean;
  isGooglePayEnabled?: boolean;
  isAlipayEnabled?: boolean;
  merchantCategoryCode?: string;
  name?: string;
  partnerName?: string;
  URL?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  marketId?: string;
  emailAddress?: string;
  phoneNumber?: string;
  applePayPaymentProcessingCertificates?: LocationApplePayPaymentProcessingCertificate[];
  applePayCertificateSigningRequests?: CertificateSigningRequest[];
  createdBy?: string;
  createdDateTime?: string;
  lastModifiedBy?: string;
  lastModifiedDateTime?: string;
}

interface Merchant {
  id: string;
  status: string;
}

export class Terminal {
  id: string;
  status: string;
  type?: string;
}

export class LocationRoute {
  scheme: string;
  enabled: boolean;
  merchantId?: string;
  processorInstance?: string;
  gatewayName?: string;
  pinned?: boolean;
}

export class LocationApplePayPaymentProcessingCertificate {
  id: string
  appleMerchantId: string
  fingerprint: string
  publicKeyHash: string
  contentOriginalEncrypted: string
  contentPEMEncrypted: string
  creationDateTime: Date
  expirationDateTime: Date
  uploadedDateTime: Date
}

export class LocationApiKey {
  id: string;
  enabled: boolean;
  jwtToken?: string; // Not part of the MCS record, but generated by the admin tool and put here for easy copying to the clipboard
  createdBy?: string;
  createdDateTime?: string;
  lastModifiedBy?: string;
  lastModifiedDateTime?: string;

  constructor(enabled: boolean = true) {
    this.enabled = enabled;
  }
}

export class JWT {
  jwt: string;
}

export function formatUtcIsoStringToLocal(isoUtcString: string): string {
  const date = new Date(isoUtcString);

  return date.toLocaleString(
    undefined, // Use default locale of the user
    {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short'
    }
  )
}

export function getGatewayName(location: Location): Gateway {
  let gateway = Gateway.PayVector;
  if (!Location || !location.routes || location.routes.length === 0) {
    return gateway;
  }
  for (const route of location.routes) {
    if (!route.enabled) { continue }
    if (getTargetSchemes(location.routes, false).indexOf(route.scheme as KnownRoutes) < 0) {
      continue;
    }
    if (route.gatewayName == "Cybersource") {
      return Gateway.Cybersource;
    }
    if (route.gatewayName == "SmartVista" || route.gatewayName == "Mars") {
      gateway = Gateway.Mars;
    }
  }
  return gateway
}

export function isActive(location: Location): boolean {
  return location?.status !== Status.Closed;
}

export function getAmexAccountType(location: Location): AmexAccountType {
  let amexRoute = getAmexRoute(location);
  if (!amexRoute || !amexRoute.enabled) {
    return AmexAccountType.None;
  }
  if (amexRoute.gatewayName == Gateway.Mars) {
    if (!amexRoute.merchantId || amexRoute.merchantId === '' || amexRoute.merchantId === location.id) {
      return AmexAccountType.PayFac;
    }
  }
  return AmexAccountType.Direct;
}

export function getAmexSENumber(location: Location): string {
  let accountType = getAmexAccountType(location);
  if (accountType === AmexAccountType.Direct) {
    let amexRoute = getAmexRoute(location);
    if (amexRoute && amexRoute.merchantId) {
      return amexRoute.merchantId;
    }
  }
  return '';
}

export function getAmexF2FDirectSENumber(location: Location): string | undefined {
  if (!location || !location.routes || location.routes.length === 0) {
    return undefined;
  }
  for (const route of location.routes) {
    if (route.scheme == KnownRoutes.AmexF2FDirect.toString()) {
      return route.merchantId;
    }
  }
  return undefined;
}

export function getAmexRoute(location: Location): LocationRoute | undefined {
  if (!location || !location.routes || location.routes.length === 0) {
    return undefined;
  }
  let r: LocationRoute | undefined = undefined;
  for (const route of location.routes) {
    if (route.scheme === 'amex_ecom') {
      return route;
    }
    if (route.scheme === 'amex') {
      // this will only be returned if `amex_ecom` not found
      r = route;
    }
  }
  return r;
}

